import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { v4 as uuid } from 'uuid';
import { useHistory, useRouteMatch } from 'react-router';
import { generatePath } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Field, Form, Formik } from 'formik';

import lineEndpoint from 'app/api/services/lineEndpoint';

import notification from 'app/config/notification';
import useShowFormErrors from '../hooks/useShowFormErrors';
import helpers from 'app/utils/helpers';
import { errorHandler } from 'app/utils/errorHandler';
import { VIEW_CTX } from '../constants';
import { URLS } from 'app/config/urls';
import { validateFormikForm } from 'app/utils/forms/functions';

import {
    clearAllSubstituteConductors,
    clearFares,
    clearGrants,
    clearLineAdictives,
    clearLineVehicles,
    clearPermissionaries,
    clearPermissionaryCepe,
    clearPermissionaryConductors,
    clearSelectedLine,
    clearSelectedPermissionary,
    fetchCepe,
    fetchGrants,
    fetchLine,
    fetchPermissionary,
    fetchPermissionaryConductors,
    fetchPermissionaryRefData,
    setLineVehicles,
    setPermissionaryConductors,
} from 'app/store/slices/line';
import { clearInspectedVehicles } from 'app/store/slices/vehicle';
import { clearDocumentTypes, fetchDocumentTypes } from 'app/store/slices/forms';

import { makeStyles } from '@material-ui/core';

import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import Drawer from '@material-ui/core/Drawer';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';

import Loading from 'app/layouts/Loading';
import ConfirmDialog from 'app/components/ConfirmDialog';
import PageWrapper from '../components/PageWrapper';
import SaveBox from '../components/SaveBox';
import DefaultTable from '../components/DefaultTable';
import AddButton from '../components/AddButton';
import SimpleAlert from '../components/SimpleAlert';
import CustomTextField from '../components/TextField';
import CustomDateField from '../components/DateField';
import CustomMaskField from '../components/MaskField';

import FindPermissionary from './drawers/FindPermissionary';
import FindVehicle from './drawers/FindVehicle';
import AddAdictives from './drawers/AddAddictives';
import FindConductor from './drawers/FindConductor';
import ActivateVehicleModal from './modals/ActivateVehicleModal';
import DocumentGenerationModal from './modals/DocumentGenerationModal';

import {
    adictiveColumns,
    attachmentColumns,
    permissionaryVehicleListColumns,
    permiSubstituteConductorColumns,
} from '../table-configs/columns-schemas';
import { preparePayload } from '../form-configs/form-helpers';
import { permissionariesValidationSchema } from '../form-configs/validation-schemas';
import { permissionariesInitialValues } from '../form-configs/permissionaries-initial-values';
import { applyBusinessRulesToContext } from '../table-configs/permissionaries-details-helpers';

import styles from './styles';

const ERROR_MSG = {
    IS_VEHICLE_ACTIVE: 'Não é possível apagar o veículo ativo. Por favor, desative o veículo antes de continuar.',
    ALREADY_HAS_ACTIVE:
        'Já existe um veículo ativo na lista de veículo(s). Para adicionar outro veículo, inative o veículo atual da lista',
    HAS_PENDENCIES: 'Existem pendências a serem resolvidas. Favor verificar.',
};

function notAuthorizedNotification(vehicleDePlaca, permiName) {
    return `O veículo de placa ${vehicleDePlaca} está no CEPE do permissionário ${permiName}. Favor ajustar para ativar o veículo`;
}

const DEFAULT_ATTACHMENT = {
    cdDocumentoPermissionario: null,
    uid: null,
    canEdit: false,
    lockSelect: false,
    arquivo: '',
    idFile: '',
    deDescricao: '',
    fileName: '',
    tipoDocumento: null,
};

const PermissionariesDetails = () => {
    const history = useHistory();
    const match = useRouteMatch();
    const classes = useStyles();
    const dispatch = useDispatch();

    const isEmpresa = React.useRef(false);
    const screenIsMounted = React.useRef();
    const cdLinhaFromPath = React.useRef(match.params.cdLinha).current;
    const cdPermissionarioFromPath = React.useRef(match.params.cdPermissionario).current;

    const pendenciesField = React.useRef(null);
    const topPage = React.useRef(null);

    const { scrollToErrors, formElement } = useShowFormErrors();

    const [loading, setLoading] = useState(false);
    const [loadingProcess, setLoadingProcess] = useState(false);
    const [uploadingDocument, setUploadingDocument] = useState(false);
    const [loadingPendencies, setLoadingPendencies] = useState(false);
    const [loadingTable, setLoadingTable] = useState(false);

    const [editMode, setEditMode] = useState(false);
    const [formLockedMessage, setFormLockedMessage] = useState(false);
    const [readOnlyMode, setReadOnlyMode] = useState(false);

    const [openDrawer, setOpenDrawer] = useState(false);
    const [selectedViewContext, setSelectedViewContext] = useState(null);
    const [alertMessage, setAlertMessage] = useState(null);
    const [openConfirm, setOpenConfirm] = useState(false);
    const [confirmMessage, setConfirmMessage] = useState(null);
    const [resourceContext, setResourceContext] = useState(null);
    const [openCepeModal, setOpenCepeModal] = useState(false);
    const [openActivateVehicleModal, setOpenActivateVehicleModal] = useState(false);

    const [selectedResource, setSelectedResource] = useState(null);

    const [formInitials, setFormInitials] = useState(permissionariesInitialValues);
    const [cdProcesso, setCdProcesso] = useState('');
    const [nuProcesso, setNuProcesso] = useState('');
    const [setSolicitacao] = useState(null);
    const [nuSolicitacaoFmt, setNuSolicitacaoFmt] = useState('');
    const [cdSolicitacao, setCdSolicitacao] = useState('');
    const [pendenciasSistema, setPendenciasSistema] = useState(null);
    const [deCpfCnpj, setDeCpfCnpj] = useState('');
    const [cpfCnpjErrorMessage, setCpfCnpjErrorMessage] = useState('');
    const [alreadyValidatedCpfCnpj, setAlreadyValidatedCpfCnpj] = useState(false);

    const [lineVehiclesRows, setLineVehiclesRows] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [addictivesRows, setAddictivesRows] = useState([]);
    const [itineraries, setItineraries] = useState(null);

    const [exigirSave, setExigirSave] = useState(false);

    const filteredAddictivesRows = useMemo(
        () => addictivesRows.filter((aditivo) => !aditivo.boExcluido),
        [addictivesRows],
    );

    const [selectedAddictiveToEdit, setSelectedAddictiveToEdit] = useState(null);
    const [selectedVehicleToEdit, setSelectedVehicleToEdit] = useState(null);

    const lineGrants = useSelector((state) => state.line.grants);
    const lineVehicles = useSelector((state) => state.line.lineVehicles);
    const selectedLine = useSelector((state) => state.line.selected);
    const documentTypes = useSelector((state) => state.forms.documentTypes);
    const selectedPermissionary = useSelector((state) => state.line.selectedPermissionary);
    const permissionaryCepe = useSelector((state) => state.line.permissionaryCepe);
    const permissionaryConductors = useSelector((state) => state.line.permissionaryConductors);
    const permissionaryRefData = useSelector((state) => state.line.permissionaryRefData);

    const hasCdLinhaPermi = Boolean(selectedPermissionary?.cdLinhaPermissionario);
    const isPermissionaryActive = selectedPermissionary?.boAtivo;
    const hasPendencies = Boolean(selectedPermissionary?.dePendencias || pendenciasSistema);
    const permiHasCEPE = Boolean(permissionaryCepe?.cdCepe);
    const vehicleIdFromCepe = permissionaryCepe?.cdVeiculo;

    const canGenerateCEPE = useMemo(() => {
        return true;
    }, []);

    //#region EFFECTS

    useEffect(() => {
        screenIsMounted.current = true;
        (async () => {
            setLoading(true);
            setLoadingPendencies(true);

            const defaultParams = { page: 0, size: 1, request: { cdLinhaFromPath } };
            const promisses = [];

            if (lineGrants.length === 0) promisses.push(dispatch(fetchGrants()));
            if (documentTypes.length === 0) promisses.push(dispatch(fetchDocumentTypes()));
            if (permissionaryRefData.length === 0) promisses.push(dispatch(fetchPermissionaryRefData()));
            if (!selectedLine) promisses.push(dispatch(fetchLine(cdLinhaFromPath)));

            if (cdPermissionarioFromPath) {
                if (!selectedPermissionary) promisses.push(dispatch(fetchPermissionary(cdPermissionarioFromPath)));
                if (permissionaryConductors.length === 0)
                    promisses.push(dispatch(fetchPermissionaryConductors(cdPermissionarioFromPath)));
            }

            if (promisses.length > 0) await Promise.all(promisses);

            if (selectedPermissionary) {
                if (selectedPermissionary?.empresa) {
                    isEmpresa.current = true;
                }
                const systemPendencies = await fecthPendencies(selectedPermissionary);
                setPendenciasSistema(() => systemPendencies);
                setFormInitials((prev) => ({ ...prev, ...selectedPermissionary }));
                setDeCpfCnpj(() => helpers.formatarDocumento(selectedPermissionary?.deCpfCnpj));
            }

            if (!itineraries) {
                const itinearyRes = await lineEndpoint.getItineraries(defaultParams);
                if (itinearyRes && itinearyRes?.status === 200) {
                    setItineraries(() => itinearyRes.data);
                }
            }
            setLoadingPendencies(false);
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPermissionary]);

    useEffect(() => {
        return () => {
            screenIsMounted.current = false;
            dispatch(clearSelectedLine());
            dispatch(clearGrants());
            dispatch(clearDocumentTypes());
            dispatch(clearFares());
            dispatch(clearPermissionaryCepe());
            dispatch(clearInspectedVehicles());
            dispatch(clearPermissionaryConductors());
            dispatch(clearAllSubstituteConductors());
            dispatch(clearPermissionaries());
            dispatch(clearLineVehicles());
            dispatch(clearLineAdictives());
            dispatch(clearSelectedPermissionary());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!cdPermissionarioFromPath) return;
        if (!selectedPermissionary) return;
        (async () => {
            try {
                setLoadingTable(true);

                if (selectedPermissionary?.nuProcesso) {
                    const numero = selectedPermissionary.nuProcesso;
                    setNuProcesso(numero);

                    const processRes = await lineEndpoint.getProcess({ numero });
                    const process = processRes.data;

                    if (process && process.id) {
                        setCdProcesso(() => process.id);

                        const requestRes = await lineEndpoint.getRequestByProcessId({ id: process.id });
                        const solicitacao = requestRes.data;
                        if (solicitacao) {
                            setSolicitacao(solicitacao);
                            setNuSolicitacaoFmt(solicitacao.nuSolicitacaoFmt);
                        }
                    }
                }

                if (selectedPermissionary.documentos?.length > 0) {
                    const mappedDocs = selectedPermissionary.documentos.map((doc) => {
                        return { ...doc, canEdit: true, lockSelect: true };
                    });
                    setAttachments(mappedDocs);
                }

                if (selectedPermissionary.aditivos?.length > 0) {
                    setAddictivesRows(selectedPermissionary.aditivos);
                }

                if (selectedPermissionary.veiculos?.length > 0) {
                    const temp = _.cloneDeep(selectedPermissionary.veiculos);
                    dispatch(setLineVehicles(temp));
                }
            } catch (error) {
                errorHandler(error.response?.status, error.response?.data?.message);
            } finally {
                setLoadingTable(false);
            }
        })();

        return () => {
            dispatch(clearLineVehicles());
            dispatch(clearLineAdictives());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPermissionary, cdPermissionarioFromPath, dispatch, isEmpresa]);

    useEffect(() => {
        if (lineVehicles) setLineVehiclesRows(lineVehicles.filter((entry) => !entry.boExcluido));
    }, [lineVehicles]);

    useEffect(() => {
        if (!cdPermissionarioFromPath) return;
        (async () => {
            if (!permissionaryCepe && cdPermissionarioFromPath) {
                await dispatch(fetchCepe(cdPermissionarioFromPath));
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //#endregion

    const openDrawerWithContext = (context) => {
        const contextRule = applyBusinessRulesToContext({ context: context, list: lineVehicles });
        if (contextRule.authorized) {
            setSelectedViewContext(context);
            setOpenDrawer(true);
        } else {
            setAlertMessage(contextRule.errorMessage);
        }
    };

    const openConfirmDeleteWithContext = ({ data, context }) => {
        const mapResource = {
            [VIEW_CTX.vehicles]: 'o veículo',
            [VIEW_CTX.substituteConductor]: 'o condutor substituto',
            [VIEW_CTX.addictive]: 'o aditivo',
        };
        const message = `Deseja excluir ${mapResource[context]} da lista?`;
        setResourceContext(context);
        setSelectedResource(data);
        setConfirmMessage(message);
        setOpenConfirm(true);
    };

    const deleteSelectedResource = () => {
        const mapDeleteFn = {
            [VIEW_CTX.vehicles]: handleDeleteVehicle,
            [VIEW_CTX.substituteConductor]: handleDeleteSubstituteConductor,
            [VIEW_CTX.addictive]: handleDeleteAddictive,
        };
        mapDeleteFn[resourceContext](null, selectedResource);
    };

    function loadPermissionaryData(permissionaryData, formikBag) {
        setCpfCnpjErrorMessage('');
        const deCpfCnpjFmt = helpers.formatarDocumento(permissionaryData?.deCpfCnpj);
        setAlreadyValidatedCpfCnpj(() => true);
        formikBag.setFieldValue('nuPermissionario', permissionaryData.nuPermissionario);
        formikBag.setFieldValue('deNomePermissionario', permissionaryData.deNomePermissionario);
        setDeCpfCnpj(() => deCpfCnpjFmt);
        setEditMode(true);
    }

    async function isActiveInAnotherLine(permissionaryData) {
        let cdLinha = permissionaryData.cdLinha;
        let MAX_REGISTROS = 100; // número arbitrado para quantidade de linhas retornadas max.
        const params = { page: 0, size: MAX_REGISTROS, request: { cdLinha } };
        if (+cdLinha === +cdLinhaFromPath) {
            return false;
        }
        const lineResponse = await lineEndpoint.getPermissionaries(params);
        const linePermissionaries = lineResponse?.data?.content;
        if (lineResponse.status === 200 && linePermissionaries.length > 0) {
            let permiExists = verifyIfExists(linePermissionaries, permissionaryData);
            if (permiExists) {
                // Se existe ativo em outra linha, informar ao usuário
                const res = await lineEndpoint.getLine(cdLinha);

                let nomeLinha = res?.data?.deDescricao;
                let nomePermissionario = permissionaryData.deNomePermissionario;
                setAlertMessage(
                    `O permissionário ${nomePermissionario} já está ativo na linha ${nomeLinha}. Inative o permissionário antes de continuar.`,
                );
                setFormLockedMessage(
                    `O permissionário ${nomePermissionario} já está ativo na linha ${nomeLinha}. Inative o permissionário antes de continuar.`,
                );
                return true;
            }
        }
        if (formLockedMessage) setFormLockedMessage(null);
        return false;
    }

    const handleSetSelectedPermissionary = async (permissionaryData, formikBag) => {
        if (formLockedMessage) setFormLockedMessage(null);
        if (!permissionaryData.cdLinha || permissionaryData.cdLinha === cdLinhaFromPath) {
            loadPermissionaryData(permissionaryData, formikBag);
            return;
        }

        if (permissionaryData.cdLinha && permissionaryData.cdLinha !== cdLinhaFromPath) {
            setLoading(true);
            const alreadyUsed = await isActiveInAnotherLine(permissionaryData);
            if (!alreadyUsed) loadPermissionaryData(permissionaryData, formikBag);
            setLoading(false);
        }
    };

    //#region TABELA VEÍCULO DO PERMISSIONÁRIO

    const handleSetSelectedVehicle = async (vehicleData) => {
        const tempOldData = _.cloneDeep(lineVehicles);
        setLoading(true);
        if (await canActivateVehicle(vehicleData, permissionaryCepe)) {
            dispatch(setLineVehicles([...tempOldData, vehicleData]));
        }
        setLoading(false);
    };

    const canActivateVehicle = async (vehicleObj) => {
        const vehicle = vehicleObj.veiculo;
        const response = await lineEndpoint.getCepeByCdVeiculo(vehicle.cdVeiculo);
        const cepePermi = response?.data;
        const vehicleCepe = response?.data?.cepe;
        // SE O VEICULO NÃO TEM CEPE.
        if (!vehicleCepe) {
            return true;
        }
        // SE O Permissionario do Cepe do Veiculo for este mesmo permissionário
        if (
            selectedPermissionary &&
            vehicleCepe.cepe?.cdLinhaPermissionario === selectedPermissionary.cdLinhaPermissionario
        ) {
            return true;
        }
        notification(notAuthorizedNotification(vehicle.dePlaca, cepePermi.deNomePermissionario), {
            toastId: 'NOT_AUTHORIZED_ACTIVATE_VEHICLE',
            type: 'error',
        });
        return false;
    };

    const handleToggleActiveVehicle = async ({ data: vehicleData }) => {
        const isActive = vehicleData?.boAtivo === true;
        const isVehicleFromCEPE = vehicleIdFromCepe === vehicleData?.cdVeiculo;
        const isFromPermi = selectedPermissionary?.cdLinhaPermissionario === vehicleData?.cdLinhaPermissionario;

        const alreadyHasActive = lineVehicles.some((vehicle) => vehicle.boAtivo);

        // Se o veículo não está ativo e
        if (!isActive && alreadyHasActive) {
            notification(ERROR_MSG.ALREADY_HAS_ACTIVE, {
                toastId: 'ALREADY_HAS_ACTIVE',
                type: 'error',
            });
            return;
        }

        // Se o veículo está ativo, ao desativar, modal -> desativar
        if (isActive) {
            setSelectedVehicleToEdit(vehicleData);
            setOpenActivateVehicleModal(true);
            return;
        }

        // Se o veículo está inativo, já tem CEPE e é do permissionário, ao ativar, ativar e remover dados da inativação
        if (isFromPermi && !isActive && permiHasCEPE) {
            setLoading(true);
            let authorized = await canActivateVehicle(vehicleData, permissionaryCepe);
            setLoading(false);
            if (!authorized) return;

            const updatedList = lineVehicles.map((stateVehicle) => {
                if (stateVehicle.cdVeiculo === vehicleData.cdVeiculo) {
                    return {
                        ...stateVehicle,
                        boAtivo: true,
                        dtInativacao: null,
                        deObservacaoInativacao: null,
                    };
                }
                return { ...stateVehicle, boAtivo: false };
            });
            dispatch(setLineVehicles(updatedList));
            return;
        }

        // Se o veículo está ativo, não tem CEPE e já é do permissionário, ao desativar, desativar
        if (isFromPermi && isActive && !isVehicleFromCEPE) {
            return deactivatePermiVehicle(lineVehicles, vehicleData, dispatch);
        }

        // Se o veículo está inativo, não tem CEPE e já é do permissionário, ao ativar, ativar
        if (isFromPermi && !isActive && !permiHasCEPE) {
            setLoading(true);
            const authorized = await canActivateVehicle(vehicleData, permissionaryCepe);
            setLoading(false);
            if (!authorized) return;

            return activatePermiVehicle(lineVehicles, vehicleData, dispatch);
        }

        // Se o veículo está ativo e não é do permissionário, ao desativar, desativar
        if (!isFromPermi && isActive) {
            return deactivatePermiVehicle(lineVehicles, vehicleData, dispatch);
        }

        // Se o veículo está inativo e não é do permissionário, ao ativar, ativar
        if (!isFromPermi && !isActive) {
            setLoading(true);
            const authorized = await canActivateVehicle(vehicleData, permissionaryCepe);
            setLoading(false);
            if (!authorized) return;
            return activatePermiVehicle(lineVehicles, vehicleData, dispatch);
        }
    };

    const handleOpenVehicleDetails = (event, data) => {
        setReadOnlyMode(true);
        setSelectedVehicleToEdit(() => data);
        setOpenActivateVehicleModal(true);
    };

    const handleDeleteVehicle = (event, data) => {
        if (data.boAtivo) {
            setAlertMessage(ERROR_MSG.IS_VEHICLE_ACTIVE);
            return;
        }

        let updatedList;

        if (data?.cdLinhaPermissionario === selectedPermissionary?.cdLinhaPermissionario) {
            updatedList = lineVehicles?.map((entry) => {
                const isSelectedVehicle = entry.cdVeiculo === data.cdVeiculo;
                const hasBoAtivo = entry.boAtivo === true;

                if (isSelectedVehicle && !hasBoAtivo) {
                    return { ...entry, boExcluido: true };
                } else {
                    return entry;
                }
            });
        } else {
            updatedList = lineVehicles?.filter((entry) => {
                return entry.cdVeiculo !== data.cdVeiculo;
            });
        }

        dispatch(setLineVehicles(updatedList));
        setExigirSave(true);
    };
    //#endregion

    //#region TABELA CONDUTOR SUBSTITUTO
    const handleEditSubstituteConductor = (e, data) => {
        const url = generatePath(URLS.LINES_MANAGE_CONDUCTORS_EDIT, {
            cdLinha: cdLinhaFromPath,
            cdCondutorSubstituto: data.cdCondutorSubstituto,
        });
        history.push(url);
    };

    const handleDeleteSubstituteConductor = (event, data) => {
        const updated = permissionaryConductors.filter((cond) => {
            return cond.cdCondutorSubstituto !== data.cdCondutorSubstituto;
        });
        dispatch(setPermissionaryConductors(updated));
    };
    //#endregion

    //#region TABELA DE ADITIVOS
    const handleSetAddictives = (data) => {
        const isEditing = addictivesRows.some((adt) => adt.cdAditivo === data.cdAditivo);

        if (isEditing) {
            const updated = addictivesRows.map((adt) => (adt.cdAditivo === data.cdAditivo ? data : adt));
            setAddictivesRows(updated);
        } else {
            const valuesCopy = _.cloneDeep(data);
            const cdTipoDocumento = parseInt(data.cdTipoDocumento);
            valuesCopy.tipoDocumento = _.find(documentTypes, { cdTipoDocumento });
            setAddictivesRows((prevState) => [...prevState, valuesCopy]);
        }
    };

    const handleEditAddictive = (e, data) => {
        setSelectedAddictiveToEdit(data);
        openDrawerWithContext(VIEW_CTX.addictive);
    };

    const handleDeleteAddictive = (event, data) => {
        let updatedList = [];

        updatedList = addictivesRows?.map((entry) => {
            const isSelectedAddictive = entry.cdAditivo === data.cdAditivo;
            if (isSelectedAddictive) {
                return { ...entry, boExcluido: true };
            }
            return entry;
        });
        setAddictivesRows(() => updatedList);
    };
    //#endregion

    //#region AUXILIARES

    const handleCloseDrawer = () => {
        setSelectedAddictiveToEdit(null);
        setAlertMessage(null);
        setOpenDrawer(false);
    };

    const showNotification = (msg, type) => {
        notification(msg, {
            toastId: 'VALIDATION',
            type,
        });
    };

    const searchProcess = async (e) => {
        setLoadingProcess(true);
        let processNumber = e.target.value;
        if (processNumber && processNumber !== '') {
            try {
                const response = await lineEndpoint.getProcess({ numero: processNumber });
                const process = response.data;
                if (!process || !process.id || process.id === '') {
                } else {
                    const res = await lineEndpoint.getRequestByProcessId({ id: response.data.id });
                    const request = res.data;
                    if (!request) {
                        setNuSolicitacaoFmt('');
                        setCdSolicitacao('');
                    } else {
                        setNuSolicitacaoFmt(res.data.nuSolicitacaoFmt);
                        setCdSolicitacao(res.data.cdSolicitacao);
                    }
                    setNuProcesso(response.data.numero);
                    setCdProcesso(response.data.id);
                }
            } catch (error) {
                showNotification(error.message, 'error');
            }
        }
        setLoadingProcess(false);
    };

    const handleConfirmDeleteModal = (deleteCallback) => {
        setOpenConfirm(false);
        return deleteCallback;
    };

    const validateDocument = (value) => {
        let isValid = helpers.validateCpfCnpj(value);
        if (!Boolean(value.trim())) {
            setCpfCnpjErrorMessage('Campo obrigatório');
            return false;
        } else if (!isValid) {
            setCpfCnpjErrorMessage('CPF ou CNPJ inválido');
            return false;
        } else {
            setCpfCnpjErrorMessage('');
            setDeCpfCnpj(() => helpers.formatarDocumento(value));
            return true;
        }
    };

    const searchPermiByDocument = async (value, formikBag) => {
        try {
            setLoading(true);
            if (formLockedMessage) setFormLockedMessage(null);
            let temp = helpers.somenteAlfanumericos(value);
            const response = await lineEndpoint.getCompanyOrPermissionaryByDocument(temp);
            if (response.data && Object.keys(response.data).length > 0) {
                let foundObj = response.data;
                if (foundObj.cdLinhaPermissionario) {
                    if (await isActiveInAnotherLine(foundObj)) {
                        return;
                    }
                }
                setDeCpfCnpj(helpers.formatarDocumento(foundObj.deCpfCnpj));
                formikBag.setFieldValue('nuPermissionario', foundObj.nuPermissionario);
                formikBag.setFieldValue('deNomePermissionario', foundObj.deNomePermissionario);
                if (foundObj.empresa) {
                    isEmpresa.current = true;
                    setEditMode(true);
                }
            } else {
                setAlreadyValidatedCpfCnpj(() => false);
            }
        } catch (error) {
            showNotification(error.message, 'warning');
        } finally {
            setLoading(false);
        }
    };

    //#endregion

    //#region SUBMISSÃO OU CANCELAMENTO DO CADASTRO E GERAÇÃO DE CEPE

    const handleGoBack = () => {
        dispatch(clearLineVehicles());
        dispatch(clearLineAdictives());
        dispatch(clearPermissionaryConductors());
        dispatch(clearPermissionaries());
        history.push(generatePath(URLS.LINES_MANAGE_PERMISSIONARIES, { cdLinha: cdLinhaFromPath }));
    };

    async function validateFormAndCallFunction(formProps, cb) {
        formProps.setFieldTouched('nuProcesso');
        if (!alreadyValidatedCpfCnpj) {
            validateDocument(deCpfCnpj);
        }
        validateFormikForm(formProps, permissionariesValidationSchema).then((res) => {
            if (Object.keys(res).length > 0) {
                scrollToErrors();
            } else {
                if (cb) {
                    cb();
                    setExigirSave(false);
                }
            }
        });
    }

    async function handleOpenCepeModal(event, formProps) {
        validateFormAndCallFunction(formProps, () => {
            setOpenCepeModal(true);
            if (hasPendencies || formProps.values['dePendencias']) {
                setAlertMessage(ERROR_MSG.HAS_PENDENCIES);
            }
        });
    }

    const handleSubmitForm = async (values) => {
        try {
            if (formLockedMessage) {
                setAlertMessage(formLockedMessage);
                return;
            }
            if (cpfCnpjErrorMessage) {
                scrollToErrors();
                return;
            }

            const valuesCopy = _.cloneDeep(values);
            valuesCopy.cdLinha = selectedLine.cdLinha;
            valuesCopy.nuProcesso = nuProcesso;
            valuesCopy.cdProcesso = cdProcesso;
            valuesCopy.nuSolicitacao = nuSolicitacaoFmt;
            valuesCopy.cdSolicitacao = cdSolicitacao;
            valuesCopy.deCpfCnpj = helpers.somenteAlfanumericos(deCpfCnpj);

            if (Boolean(valuesCopy.dePendenciasSistema)) {
                valuesCopy.dePendenciasSistema = pendenciasSistema;
            }

            if (lineVehicles.length > 0) {
                valuesCopy.veiculos = preparePayload(lineVehicles, [
                    'cdVeiculo',
                    'boAtivo',
                    'boExcluido',
                    'deObservacaoInativacao',
                    'dtInativacao',
                    'cdLinhaPermissionario',
                    'cdVeiculoPermissionario',
                ]);
            } else {
                valuesCopy.veiculos = [];
            }

            if (permissionaryConductors && permissionaryConductors.length > 0) {
                valuesCopy.condutoresSubstitutos = permissionaryConductors;
            } else {
                valuesCopy.condutoresSubstitutos = [];
            }

            if (addictivesRows.length > 0) {
                valuesCopy.aditivos = addictivesRows;
            } else {
                valuesCopy.aditivos = [];
            }
            if (attachments.length > 0) {
                valuesCopy.documentos = attachments;
            } else {
                valuesCopy.documentos = [];
            }
            setLoading(true);
            const savedPermissionary = await lineEndpoint.savePermissionary(valuesCopy);
            if (savedPermissionary.status === 200) {
                if (!cdPermissionarioFromPath) {
                    let cdPermissionario = savedPermissionary.data.cdLinhaPermissionario;
                    let cdLinha = cdLinhaFromPath;
                    history.push(generatePath(URLS.LINES_MANAGE_PERMISSIONARIES_EDIT, { cdLinha, cdPermissionario }));
                }
                await dispatch(fetchPermissionary(savedPermissionary.data.cdLinhaPermissionario));
                notification('Permissionário salvo com sucesso', {
                    toastId: 'SUCCESSFUL_SAVE_REQUEST',
                    type: 'success',
                });
            }
        } catch (error) {
            errorHandler(error.response?.status, error.response?.data?.message);
        } finally {
            setLoading(false);
        }
    };
    //#endregion

    return (
        <>
            <Formik
                enableReinitialize={true}
                initialValues={formInitials}
                validationSchema={permissionariesValidationSchema}
                validateOnBlur
                validateOnChange
                onSubmit={handleSubmitForm}>
                {(formProps) => {
                    return (
                        <>
                            <Form ref={formElement}>
                                <PageWrapper
                                    title="Cadastro / Edição de permissionário"
                                    actionButton={
                                        <Button
                                            disabled={isEmpresa.current}
                                            variant="contained"
                                            color="primary"
                                            disableElevation
                                            className={classes.addButton}
                                            onClick={() => openDrawerWithContext(VIEW_CTX.searchPermissionary)}>
                                            Buscar permissionário
                                        </Button>
                                    }>
                                    {loading && <Loading />}
                                    <Grid container spacing={4}>
                                        <Grid item xs={12}>
                                            <Grid item xs={12} sm={7}>
                                                <TextField
                                                    variant="filled"
                                                    name="deDescricao"
                                                    label="Linha"
                                                    fullWidth
                                                    disabled
                                                    inputProps={{
                                                        readOnly: true,
                                                    }}
                                                    InputLabelProps={{ shrink: true }}
                                                    value={selectedLine?.deDescricao}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} container spacing={2}>
                                            <Grid item xs={12} sm={5}>
                                                <Field
                                                    name="nuProcessoSei"
                                                    component={CustomTextField}
                                                    label="Número do Processo SEI"
                                                    inputProps={{
                                                        maxLength: 30,
                                                    }}
                                                    validate={(value) => {
                                                        if (!value) {
                                                            const clearNuprocesso = nuProcesso
                                                                .trim()
                                                                .replace(/\D/g, '');
                                                            if (!clearNuprocesso) {
                                                                return 'Número de processo obrigatório';
                                                            }
                                                        }
                                                    }}
                                                    onBlur={() => {
                                                        formProps.setFieldTouched('nuProcesso');
                                                        formProps.setFieldTouched('nuProcessoSei');
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={5}>
                                                <Field
                                                    name="nuProcesso"
                                                    component={CustomMaskField}
                                                    loading={loadingProcess}
                                                    value={nuProcesso}
                                                    label="Número do Processo"
                                                    mask={'9999.9999/999999'}
                                                    validate={() => {
                                                        if (!formProps.values.nuProcessoSei) {
                                                            const clearNuprocesso = nuProcesso
                                                                .trim()
                                                                .replace(/\D/g, '');
                                                            if (!clearNuprocesso) {
                                                                return 'Número de processo obrigatório';
                                                            }
                                                        }
                                                    }}
                                                    error={
                                                        formProps.errors['nuProcesso'] &&
                                                        formProps.touched['nuProcesso']
                                                    }
                                                    helperText={
                                                        formProps.touched['nuProcesso'] &&
                                                        formProps.errors['nuProcesso']
                                                    }
                                                    onBlur={(e) => {
                                                        formProps.setFieldTouched('nuProcesso');
                                                        formProps.setFieldTouched('nuProcessoSei');
                                                        setCdProcesso('');
                                                        searchProcess(e);
                                                    }}
                                                    onChange={(e) => setNuProcesso(e.target.value)}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} container spacing={2}>
                                            <Grid item xs={2}>
                                                <Field
                                                    component={CustomTextField}
                                                    label="Número de cadastro"
                                                    name="nuPermissionario"
                                                    disabled={editMode || isEmpresa.current}
                                                    maxLength="10"
                                                />
                                            </Grid>
                                            <Grid item xs={5}>
                                                <TextField
                                                    fullWidth
                                                    variant="filled"
                                                    // readOnly={editMode || isEmpresa.current}
                                                    InputLabelProps={{ shrink: true }}
                                                    validate={validateDocument}
                                                    inputProps={{ maxLength: 14 }}
                                                    label="CPF / CNPJ"
                                                    value={deCpfCnpj}
                                                    disabled={editMode || isEmpresa.current}
                                                    onBlur={() => {
                                                        if (!alreadyValidatedCpfCnpj) {
                                                            if (helpers.validateCpfCnpj(deCpfCnpj)) {
                                                                setCpfCnpjErrorMessage('');
                                                                setDeCpfCnpj(helpers.formatarDocumento(deCpfCnpj));
                                                                searchPermiByDocument(deCpfCnpj, formProps);
                                                            } else {
                                                                setCpfCnpjErrorMessage('CPF ou CNPJ inválido');
                                                            }
                                                        }
                                                    }}
                                                    onChange={(e) => setDeCpfCnpj(e.target.value)}
                                                    error={Boolean(cpfCnpjErrorMessage.trim())}
                                                    helperText={cpfCnpjErrorMessage}
                                                />
                                            </Grid>
                                            <Grid item xs={5}>
                                                <Field
                                                    disabled={editMode || isEmpresa.current}
                                                    component={CustomTextField}
                                                    label="Permissionario"
                                                    name="deNomePermissionario"
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} container spacing={2}>
                                            <Grid item xs={2}>
                                                <Field
                                                    component={CustomTextField}
                                                    label="Número do Termo"
                                                    name="nuTermo"
                                                    maxLength="10"
                                                />
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Field
                                                    component={CustomDateField}
                                                    label="Data assinatura"
                                                    name="dtAssinatura"
                                                />
                                            </Grid>
                                            <Grid item xs={4}>
                                                <Field
                                                    component={CustomTextField}
                                                    label="Outorga"
                                                    name="cdLinhaOutorga"
                                                    select
                                                    loading={loading}
                                                    disabled={loading}
                                                    SelectProps={{
                                                        native: true,
                                                    }}>
                                                    <option value="">Selecione...</option>
                                                    {lineGrants?.map((type, index) => (
                                                        <option key={`type-${index}`} value={type.cdLinhaOutorga}>
                                                            {type.deLinhaOutorga}
                                                        </option>
                                                    ))}
                                                </Field>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} container spacing={2}>
                                            <Grid item xs={3}>
                                                <Field
                                                    component={CustomDateField}
                                                    label="Data de publicação"
                                                    name="dtPublicacao"
                                                />
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Field
                                                    component={CustomDateField}
                                                    label="Data início da vigência"
                                                    name="dtInicioVigencia"
                                                />
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Field
                                                    component={CustomDateField}
                                                    label="Data de vencimento"
                                                    name="dtVencimento"
                                                />
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Field
                                                    component={CustomTextField}
                                                    label="A partir de"
                                                    name="cdPermissionarioRefData"
                                                    select
                                                    loading={loading}
                                                    disabled={loading}
                                                    SelectProps={{
                                                        native: true,
                                                    }}>
                                                    {permissionaryRefData.map((entry, index) => {
                                                        return (
                                                            <option
                                                                key={`entry-${index}`}
                                                                value={entry.cdPermissionarioRefData}>
                                                                {entry.dePermissionarioRefData}
                                                            </option>
                                                        );
                                                    })}
                                                </Field>
                                            </Grid>
                                            <Grid item xs={12} container spacing={2}>
                                                <Grid item xs={2}>
                                                    <Field
                                                        component={CustomTextField}
                                                        label="Prazo"
                                                        name="nuPrazo"
                                                        maxLength="10"
                                                    />
                                                </Grid>
                                                <Grid item xs={2}>
                                                    <Field
                                                        component={CustomTextField}
                                                        name="deTipoPrazo"
                                                        select
                                                        SelectProps={{
                                                            native: true,
                                                        }}>
                                                        <option value="anos">Ano(s)</option>
                                                        <option value="meses">Mes(es)</option>
                                                    </Field>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} container spacing={2}>
                                            <Grid item xs={3}>
                                                <Field
                                                    component={CustomTextField}
                                                    label="Número de habilitação"
                                                    name="deCnh"
                                                    maxLength="11"
                                                />
                                            </Grid>
                                            <Grid item xs={3}>
                                                <Field
                                                    component={CustomTextField}
                                                    label="Categoria CNH"
                                                    name="tpCategoriaCnh"
                                                    select
                                                    SelectProps={{
                                                        native: true,
                                                    }}>
                                                    <option value="" />
                                                    <option value="A">A</option>
                                                    <option value="B">B</option>
                                                    <option value="AB">AB</option>
                                                    <option value="C">C</option>
                                                    <option value="AC">AC</option>
                                                    <option value="D">D</option>
                                                    <option value="AD">AD</option>
                                                    <option value="E">E</option>
                                                    <option value="AE">AE</option>
                                                </Field>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Grid item xs={12}>
                                                <Field
                                                    component={CustomTextField}
                                                    name="deObservacao"
                                                    label="Observação"
                                                    multiline
                                                    minRows={4}
                                                    inputProps={{
                                                        maxLength: 2000,
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                label="Pendências do sistema"
                                                variant="outlined"
                                                fullWidth
                                                multiline
                                                minRows={2}
                                                inputProps={{ readOnly: true }}
                                                InputProps={{
                                                    startAdornment: loadingPendencies ? (
                                                        <CircularProgress size={24} color="inherit" />
                                                    ) : null,
                                                }}
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                disabled={!Boolean(pendenciasSistema)}
                                                value={pendenciasSistema || ''}
                                                inputRef={pendenciesField}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Field
                                                component={CustomTextField}
                                                name="dePendencias"
                                                label="Outras pendências"
                                                multiline
                                                minRows={4}
                                                maxLength="5000"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <DefaultTable
                                                loading={loadingTable}
                                                title="Lista de veículo(s)"
                                                columns={permissionaryVehicleListColumns}
                                                rows={lineVehiclesRows}
                                                onDelete={(e, data) =>
                                                    openConfirmDeleteWithContext({ context: VIEW_CTX.vehicles, data })
                                                }
                                                entryProps={{
                                                    onChangeSelect: handleToggleActiveVehicle,
                                                    onOpenDetails: handleOpenVehicleDetails,
                                                }}
                                                actionButton={
                                                    <AddButton
                                                        disabled={loadingTable || !deCpfCnpj}
                                                        className={classes.addButton}
                                                        onClick={() => {
                                                            if (exigirSave) {
                                                                showNotification(
                                                                    'É necessário salvar as alterações da lista de veículos.',
                                                                    'error',
                                                                );
                                                                return;
                                                            }
                                                            openDrawerWithContext(VIEW_CTX.vehicles);
                                                        }}>
                                                        Adicionar
                                                    </AddButton>
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <DefaultTable
                                                loading={loadingTable}
                                                title="Condutor substituto"
                                                columns={permiSubstituteConductorColumns}
                                                rows={permissionaryConductors}
                                                onEdit={handleEditSubstituteConductor}
                                                onDelete={(e, data) =>
                                                    openConfirmDeleteWithContext({
                                                        context: VIEW_CTX.substituteConductor,
                                                        data,
                                                    })
                                                }
                                                actionButton={
                                                    <AddButton
                                                        disabled={
                                                            permissionaryConductors.length >= 1 ||
                                                            loadingTable ||
                                                            !deCpfCnpj
                                                        }
                                                        className={classes.addButton}
                                                        onClick={() =>
                                                            openDrawerWithContext(VIEW_CTX.substituteConductor)
                                                        }>
                                                        Adicionar
                                                    </AddButton>
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <DefaultTable
                                                loading={loadingTable}
                                                title="Aditivos"
                                                columns={adictiveColumns}
                                                rows={filteredAddictivesRows}
                                                onEdit={handleEditAddictive}
                                                onDelete={(e, data) =>
                                                    openConfirmDeleteWithContext({
                                                        context: VIEW_CTX.addictive,
                                                        data,
                                                    })
                                                }
                                                actionButton={
                                                    <AddButton
                                                        disabled={loadingTable || !deCpfCnpj}
                                                        className={classes.addButton}
                                                        onClick={() => openDrawerWithContext(VIEW_CTX.addictive)}>
                                                        Adicionar
                                                    </AddButton>
                                                }
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            {selectedViewContext !== VIEW_CTX.addictive ? (
                                                <DefaultTable
                                                    loading={loadingTable}
                                                    title="Anexos"
                                                    columns={attachmentColumns}
                                                    rows={attachments.filter((attc) => !attc.boExcluido)}
                                                    onDelete={(e, data) => {
                                                        if (data.cdDocumentoPermissionario === null) {
                                                            setAttachments((prevDocs) => {
                                                                return prevDocs.filter((doc) => {
                                                                    return doc.uid !== data.uid;
                                                                });
                                                            });
                                                        } else {
                                                            setAttachments((prevDocs) => {
                                                                return prevDocs.map((doc) => {
                                                                    return doc.idFile === data.idFile
                                                                        ? { ...doc, boExcluido: true }
                                                                        : doc;
                                                                });
                                                            });
                                                        }
                                                    }}
                                                    entryProps={{
                                                        uploadingState: uploadingDocument,
                                                        idProperty: 'cdLinhaPermissionario',
                                                        idValue: selectedPermissionary?.cdLinhaPermissionario,
                                                        setUploadingState: setUploadingDocument,
                                                        documentList: documentTypes,
                                                        onSelectDocType: (newDocObj, index) => {
                                                            setAttachments((prevDocs) => {
                                                                let temp = [...prevDocs];
                                                                temp[index] = {
                                                                    ...temp[index],
                                                                    tipoDocumento: newDocObj,
                                                                    boAlterado: true,
                                                                    canEdit: Boolean(newDocObj),
                                                                };
                                                                return temp;
                                                            });
                                                        },
                                                        onDeleteFile: (docId, index) => {
                                                            setAttachments((prevDocs) => {
                                                                let temp = [...prevDocs];
                                                                temp[index] = {
                                                                    ...temp[index],
                                                                    idFile: docId,
                                                                    boAlterado: true,
                                                                    lockSelect: false,
                                                                };
                                                                return temp;
                                                            });
                                                        },
                                                        onSelectFile: (data, index) => {
                                                            setAttachments((prevDocs) => {
                                                                let temp = [...prevDocs];
                                                                let uid = temp[index].uid;
                                                                let cdDocumentoPermissionario =
                                                                    temp[index].cdDocumentoPermissionario;
                                                                temp[index] = {
                                                                    ...data,
                                                                    uid,
                                                                    cdDocumentoPermissionario,
                                                                };
                                                                return temp;
                                                            });
                                                        },
                                                    }}
                                                    actionButton={
                                                        <AddButton
                                                            className={classes.addButton}
                                                            disabled={loadingTable || !deCpfCnpj}
                                                            onClick={() => {
                                                                let newDocument = {
                                                                    ...DEFAULT_ATTACHMENT,
                                                                    uid: uuid(),
                                                                };
                                                                setAttachments((prev) => [...prev, newDocument]);
                                                            }}>
                                                            Adicionar
                                                        </AddButton>
                                                    }
                                                />
                                            ) : (
                                                <DefaultTable
                                                    loading={false}
                                                    title="Anexos"
                                                    columns={attachmentColumns}
                                                    rows={[]}
                                                />
                                            )}
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <SaveBox
                                            firstButtonLabel="Voltar"
                                            onCancel={handleGoBack}
                                            onSave={() =>
                                                validateFormAndCallFunction(
                                                    formProps,
                                                    () => formProps.handleSubmit(formProps.values),
                                                    topPage,
                                                )
                                            }
                                            lastButtonLabel={
                                                hasCdLinhaPermi && isPermissionaryActive ? 'Gerar cepe' : null
                                            }
                                            onClickLastButton={(event) =>
                                                handleOpenCepeModal(event, formProps, topPage)
                                            }
                                            middleButtonDisabled={
                                                loadingProcess || !canGenerateCEPE || formProps.isSubmitting
                                            }
                                            lastButtonDisabled={
                                                loadingProcess || !canGenerateCEPE || formProps.isSubmitting
                                            }
                                        />
                                    </Grid>
                                </PageWrapper>
                            </Form>
                            <Drawer anchor="right" open={openDrawer} onClose={handleCloseDrawer}>
                                {selectedViewContext === VIEW_CTX.vehicles && (
                                    <FindVehicle
                                        onClose={handleCloseDrawer}
                                        onSave={handleSetSelectedVehicle}
                                        permissionaryDocument={deCpfCnpj}
                                    />
                                )}
                                {selectedViewContext === VIEW_CTX.searchPermissionary && (
                                    <FindPermissionary
                                        isCompany={true}
                                        onClose={handleCloseDrawer}
                                        onSave={(selectedPermi) =>
                                            handleSetSelectedPermissionary(selectedPermi, formProps)
                                        }
                                    />
                                )}
                                {selectedViewContext === VIEW_CTX.substituteConductor && (
                                    <FindConductor onClose={handleCloseDrawer} conductors={permissionaryConductors} />
                                )}
                                {selectedViewContext === VIEW_CTX.addictive ? (
                                    <AddAdictives
                                        open={openDrawer}
                                        onClose={handleCloseDrawer}
                                        onSave={handleSetAddictives}
                                        contextClearer={() => setSelectedViewContext(null)}
                                        selectedAddictive={selectedAddictiveToEdit}
                                    />
                                ) : null}
                            </Drawer>
                            ;
                        </>
                    );
                }}
            </Formik>

            <ConfirmDialog
                open={openConfirm}
                text={confirmMessage}
                handleClickYes={() => handleConfirmDeleteModal(deleteSelectedResource(resourceContext))}
                handleClickNo={() => setOpenConfirm(false)}
            />

            <ActivateVehicleModal
                open={openActivateVehicleModal}
                onClose={() => {
                    setOpenActivateVehicleModal(false);
                    setReadOnlyMode(false);
                }}
                title={readOnlyMode ? 'Observação de inativação' : 'Desativar - veículo com CEPE ativo'}
                readOnly={readOnlyMode}
                selectedVehicle={selectedVehicleToEdit}
            />

            <DocumentGenerationModal open={openCepeModal} onClose={() => setOpenCepeModal(false)} />

            <SimpleAlert open={!!alertMessage} onClose={() => setAlertMessage(null)}>
                {alertMessage}
            </SimpleAlert>
        </>
    );
};

const useStyles = makeStyles(styles);

export default PermissionariesDetails;

// -------------------------------------------------------------- Fns -----------------/
// ------------------------------------------------------------------------------------/
const fecthPendencies = async (permissionary) => {
    if (!permissionary) return;
    const params = {
        deCpfCnpj: permissionary.deCpfCnpj,
        cdVeiculo: permissionary.veiculos?.find((v) => v.boAtivo === true)?.cdVeiculo,
    };
    const pendenciesRes = await lineEndpoint.getPermissionaryPendencies(params);
    if (pendenciesRes.data) {
        return pendenciesRes.data.join('\n');
    }
};

function activatePermiVehicle(vehicleList, selectedVehicle, dispatchFn) {
    const updatedList = vehicleList.map((stateVehicle) => {
        if (stateVehicle.cdVeiculo === selectedVehicle.cdVeiculo) {
            return { ...stateVehicle, boAtivo: true, dtInativacao: null, deObservacaoInativacao: '' };
        }
        return { ...stateVehicle, boAtivo: false };
    });
    dispatchFn(setLineVehicles(updatedList));
}

function deactivatePermiVehicle(vehicleList, selectedVehicle, dispatchFn) {
    const updatedList = vehicleList.map((stateVehicle) => {
        if (stateVehicle.cdVeiculo === selectedVehicle.cdVeiculo) {
            return { ...stateVehicle, boAtivo: false };
        } else {
            return stateVehicle;
        }
    });
    dispatchFn(setLineVehicles(updatedList));
}

function verifyIfExists(linePermissionaries, permissionaryData) {
    return linePermissionaries.some((permi) => {
        return permi.boAtivo && permi.cdLinhaPermissionario === permissionaryData.cdLinhaPermissionario;
    });
}
